/**
 * Uploads API
 * Handles file uploads with chunked resumable uploads and progress tracking
 */

import {
  apiClient,
  handleApiResponse,
  handleApiError,
} from "../config";

export interface InitUploadRequest {
  filename: string;
  file_size: number;
  content_type: string;
  shop_id: string;
  sender_name?: string;
  sender_phone?: string;
  sender_email?: string;
  chunk_size?: number;
}

export interface InitUploadResponse {
  session_id: string;
  chunk_size: number;
  total_chunks: number;
  upload_url: string;
}

export interface UploadStatusResponse {
  session_id: string;
  filename: string;
  file_size: number;
  uploaded_chunks: number;
  total_chunks: number;
  progress: number;
  is_complete: boolean;
}

export interface SimpleUploadResponse {
  success: boolean;
  document_id: number;
  filename: string;
  file_size: number;
  status: string;
  message: string;
  receipt_code?: string;
  order_token?: string;
}

export class UploadsAPI {
  /**
   * Upload a single file (uses simple upload)
   */
  static async uploadFile(
    file: File,
    shopId: string,
    metadata: {
      sender_name?: string;
      sender_phone?: string;
      sender_email?: string;
      print_copies?: number;
      print_color?: string;
      is_urgent?: boolean;
      special_instructions?: string;
      estimated_cost?: number;
      order_token?: string;
      device_id?: string;
    } = {},
    onProgress?: (progress: number) => void
  ): Promise<SimpleUploadResponse> {
    try {
      console.log('[UploadsAPI] Starting upload:', {
        filename: file.name,
        size: file.size,
        type: file.type,
        shopId,
        metadata
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('shop_id', shopId);

      if (metadata.sender_name) formData.append('sender_name', metadata.sender_name);
      if (metadata.sender_phone) formData.append('sender_phone', metadata.sender_phone);
      if (metadata.sender_email) formData.append('sender_email', metadata.sender_email);
      if (metadata.print_copies !== undefined) formData.append('print_copies', metadata.print_copies.toString());
      if (metadata.print_color) formData.append('print_color', metadata.print_color);
      if (metadata.is_urgent !== undefined) formData.append('is_urgent', metadata.is_urgent.toString());
      if (metadata.special_instructions) formData.append('special_instructions', metadata.special_instructions);
      if (metadata.estimated_cost !== undefined) formData.append('estimated_cost', metadata.estimated_cost.toString());
      if (metadata.order_token) formData.append('order_token', metadata.order_token);
      if (metadata.device_id) formData.append('device_id', metadata.device_id);

      console.log('[UploadsAPI] Sending POST to /api/v1/uploads/simple/');

      const response = await apiClient.post(
        '/api/v1/uploads/simple/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minutes for file uploads
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(percentCompleted);
            }
          },
        }
      );

      console.log('[UploadsAPI] Upload successful:', response.data);
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('[UploadsAPI] Upload failed:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      throw handleApiError(error);
    }
  }

  /**
   * Alias for uploadFile for backward compatibility
   */
  static simpleUpload = UploadsAPI.uploadFile;

  /**
   * Initialize resumable upload for large files
   */
  static async initializeUpload(
    request: InitUploadRequest
  ): Promise<InitUploadResponse> {
    try {
      const response = await apiClient.post('/api/v1/uploads/init/', request);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Upload a chunk
   */
  static async uploadChunk(
    sessionId: string,
    chunkNumber: number,
    chunkData: Blob,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('chunk_number', chunkNumber.toString());
      formData.append('chunk', chunkData);

      const response = await apiClient.patch(
        '/api/v1/uploads/chunk/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minutes for chunk uploads
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(percentCompleted);
            }
          },
        }
      );

      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Complete resumable upload
   */
  static async completeUpload(sessionId: string): Promise<SimpleUploadResponse> {
    try {
      const response = await apiClient.post('/api/v1/uploads/complete/', {
        session_id: sessionId,
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get upload status
   */
  static async getUploadStatus(sessionId: string): Promise<UploadStatusResponse> {
    try {
      const response = await apiClient.get(`/api/v1/uploads/status/${sessionId}/`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Cancel upload
   */
  static async cancelUpload(sessionId: string): Promise<void> {
    try {
      await apiClient.delete(`/api/v1/uploads/cancel/${sessionId}/`);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Upload multiple files with progress tracking
   */
  static async uploadMultipleFiles(
    files: File[],
    shopId: string,
    metadata: {
      sender_name?: string;
      sender_phone?: string;
      sender_email?: string;
      print_copies?: number;
      print_color?: string;
      is_urgent?: boolean;
      special_instructions?: string;
      estimated_cost?: number;
    } = {},
    onFileProgress?: (fileIndex: number, progress: number) => void,
    onOverallProgress?: (progress: number) => void
  ): Promise<SimpleUploadResponse[]> {
    const fileProgress: { [key: number]: number } = {};
    const totalFiles = files.length;

    const updateOverallProgress = () => {
      if (!onOverallProgress) return;
      const totalProgress = Object.values(fileProgress).reduce((acc, progress) => acc + progress, 0);
      const overallProgress = Math.round(totalProgress / totalFiles);
      onOverallProgress(overallProgress);
    };

    const uploadPromises = files.map((file, index) => {
      return this.simpleUpload(
        file,
        shopId,
        metadata,
        (progress) => {
          if (onFileProgress) {
            onFileProgress(index, progress);
          }
          fileProgress[index] = progress;
          updateOverallProgress();
        }
      ).catch(error => {
        console.error(`Failed to upload file ${file.name}:`, error);
        // Attach file info to the error for context in the calling function
        const err = new Error(`Upload failed for ${file.name}`);
        (err as any).file = file;
        (err as any).originalError = error;
        throw err;
      });
    });

    return Promise.all(uploadPromises);
  }
}

// Export default instance for convenience
export const uploadsAPI = UploadsAPI;
